import React, {
useState,
useEffect,
useMemo,
useRef
} from "react";
import {
  useLocation,
  useHistory
} from "react-router-dom";
import {
  makeStyles
} from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Tabs,
  Tab
} from "@material-ui/core";
import {
  Add,
  Edit,
  DeleteOutline,
  TrendingUp,
  CheckCircle,
  AccessTime,
  Cancel
} from "@material-ui/icons";
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from "recharts";
import {
  toast
} from "react-toastify";
import {
  getOpportunities,
  getOpportunitySummary,
  getOpportunityStages,
  getKanbanVisualConfig,
  saveKanbanVisualConfig,
  createKanbanStage,
  deleteKanbanStage,
  updateOpportunity,
  deleteOpportunity
} from "../../services/opportunities";
import OpportunityModal from "../../components/OpportunityModal";
import OpportunityCard from "./components/OpportunityCard";
import ConfirmationModal from "../../components/ConfirmationModal";
import moment from "moment";
import {
  KoraxPage,
  KoraxPageHeader,
  KoraxButton,
  KoraxStatsGrid,
  KoraxStatCard
} from "../../components/KoraxUI";

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: "1 1 auto",
    minHeight: "100%",
    paddingBottom: theme.spacing(6),
    gap: theme.spacing(2),
    overflowY: "auto",
    overscrollBehaviorX: "none",
    WebkitOverflowScrolling: "touch",

    background:
      theme.palette.type === "dark"
        ? "linear-gradient(145deg, #060D1A 0%, #0A1628 100%)"
        : "linear-gradient(145deg, #EEF5FF 0%, #F8FAFC 100%)",

    ...theme.scrollbarStyles,

    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1.2)
    }
  },

  hero: {
    maxWidth: "100%",
    boxSizing: "border-box",
    position: "relative",
    overflow: "visible",
    borderRadius: 28,
    padding: theme.spacing(2.6),
    background: "linear-gradient(135deg, #071B4D 0%, #005BFF 58%, #00A3FF 100%)",
    color: "#fff",
    boxShadow: "0 24px 58px rgba(0,102,255,0.22)",
    display: "grid",
    gridTemplateColumns: "1fr 360px",
    gap: theme.spacing(2),
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    },
    [theme.breakpoints.down("sm")]: {
      borderRadius: 22,
      padding: theme.spacing(1.6),
      gap: theme.spacing(1.6)
    }
  },
  heroGlow: {
    position: "absolute",
    right: -80,
    top: -80,
    width: 240,
    height: 240,
    borderRadius: "50%",
    background: "rgba(255,255,255,.16)",
    filter: "blur(3px)"
  },
  heroLeft: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    gap: theme.spacing(1.4),
    alignItems: "center",
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start"
    }
  },
  heroIcon: {
    width: 62,
    height: 62,
    borderRadius: 20,
    background: "rgba(255,255,255,.16)",
    color: "#fff",
    fontSize: 28,
    boxShadow: "0 14px 28px rgba(0,0,0,.18)",
    flexShrink: 0
  },
  heroTitle: {
    fontWeight: 950,
    fontSize: "1.85rem",
    lineHeight: 1.06,
    letterSpacing: "-1px",
    color: "#fff",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.42rem"
    }
  },
  heroSubtitle: {
    fontSize: ".9rem",
    lineHeight: 1.48,
    opacity: .86,
    marginTop: 8,
    maxWidth: 640,
    overflow: "visible",
    [theme.breakpoints.down("sm")]: {
      fontSize: ".76rem",
      lineHeight: 1.35
    }
  },
  heroActions: {
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      "& button": { width: "100%" }
    }
  },
  addBtn: {
    borderRadius: 14,
    fontWeight: 900,
    fontSize: ".82rem",
    textTransform: "none",
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#fff",
    padding: theme.spacing(1.1, 2.3),
    boxShadow: "0 12px 24px rgba(0,102,255,0.28)",
    "&:hover": { background: "linear-gradient(135deg, #0052CC 0%, #0088DD 100%)" }
  },
  addBtnHero: {
    background: "#fff",
    color: "#005BFF",
    "&:hover": { background: "#EFF6FF" }
  },
  heroMetrics: {
    position: "relative",
    zIndex: 1,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr 1fr"
    }
  },
  heroMetric: {
    borderRadius: 18,
    padding: theme.spacing(1.4),
    background: "rgba(255,255,255,.14)",
    border: "1px solid rgba(255,255,255,.20)",
    backdropFilter: "blur(8px)"
  },
  heroMetricValue: {
    fontWeight: 950,
    fontSize: "1.25rem",
    color: "#fff",
    wordBreak: "break-word",
    lineHeight: 1.08,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1rem"
    }
  },
  heroMetricLabel: {
    fontSize: ".68rem",
    opacity: .78,
    fontWeight: 800,
    marginTop: 4
  },

  filterNotice: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    background: "rgba(0,102,255,0.07)",
    border: "1px solid rgba(0,102,255,0.2)",
    borderRadius: 16,
    padding: theme.spacing(1, 1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch"
    }
  },

  tabsShell: {
    maxWidth: "100%",
    boxSizing: "border-box",
    borderRadius: 24,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    overflow: "visible",
    boxShadow: "0 16px 38px rgba(15,23,42,0.07)"
  },
  tabsBar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "#fff",
    "& .MuiTabs-flexContainer": { width: "100%" }
  },
  tab: {
    flex: 1,
    minWidth: 0,
    fontWeight: 900,
    fontSize: ".86rem",
    textTransform: "none",
    [theme.breakpoints.down("sm")]: {
      fontSize: ".78rem"
    },
    "&.Mui-selected": { color: "#0066FF" }
  },
  tabIndicator: {
    background: "linear-gradient(90deg,#0066FF,#00A3FF)",
    height: 4,
    borderRadius: "4px 4px 0 0"
  },

  dashboardContent: {
    padding: theme.spacing(1.8),
    overflow: "visible",
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(1.1) }
  },
  filterBar: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      "& button": { flex: 1 },
      "& .MuiTextField-root": { width: "100%" }
    }
  },
  periodBtn: {
    borderRadius: 999,
    fontWeight: 900,
    fontSize: ".76rem",
    border: `1px solid ${theme.palette.divider}`,
    padding: "9px 16px",
    cursor: "pointer",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,.04)" : "#fff",
    color: theme.palette.text.primary
  },
  periodBtnActive: {
    background: "rgba(0,102,255,0.1)",
    border: "1px solid rgba(0,102,255,0.35)",
    color: "#0066FF"
  },
  dateField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      fontSize: ".8rem",
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0066FF" }
    }
  },

  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.2),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" }
  },
  kpiCard: {
    borderRadius: 20,
    padding: theme.spacing(1.7),
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: "0 12px 28px rgba(15,23,42,0.06)",
    minWidth: 0
  },
  kpiIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    marginBottom: theme.spacing(1),
    fontSize: 18
  },
  kpiNum: {
    fontWeight: 950,
    fontSize: "1.42rem",
    lineHeight: 1.08,
    wordBreak: "break-word",
    [theme.breakpoints.down("sm")]: { fontSize: "1.18rem" }
  },
  kpiLabel: {
    fontSize: ".76rem",
    color: theme.palette.text.secondary,
    fontWeight: 800,
    marginTop: 5
  },

  chartsGrid: {
    maxWidth: "100%",
    overflow: "hidden",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" }
  },
  chartCard: {
    borderRadius: 20,
    padding: theme.spacing(1.7),
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow: "0 12px 28px rgba(15,23,42,0.05)",
    minWidth: 0
  },
  chartTitle: {
    fontWeight: 950,
    fontSize: ".92rem",
    marginBottom: theme.spacing(1.3),
    color: theme.palette.type === "dark" ? "#fff" : "#0F172A"
  },

  emptyState: {
    textAlign: "center",
    padding: theme.spacing(5),
    color: "#94A3B8",
    fontWeight: 800
  },

  tableContent: {
    padding: 0,
    overflow: "visible"
  },
  tableActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1.6, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      "& button": { width: "100%" }
    }
  },
  opportunitiesGrid: {
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(1.5),
    display: "grid",
    overflow: "visible",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: theme.spacing(1.2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      padding: theme.spacing(1)
    }
  },
  opportunityCard: {
    maxWidth: "100%",
    boxSizing: "border-box",
    borderRadius: 22,
    padding: theme.spacing(1.6),
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#FFFFFF",
    boxShadow: "0 12px 26px rgba(15,23,42,0.06)",
    minWidth: 0
  },
  opportunityTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    alignItems: "flex-start"
  },
  opportunityNameRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    minWidth: 0
  },
  opportunityAvatar: {
    width: 42,
    height: 42,
    borderRadius: 14,
    background: "linear-gradient(135deg,#0066FF,#00A3FF)",
    color: "#fff",
    fontWeight: 950,
    fontSize: ".82rem",
    flexShrink: 0
  },
  opportunityName: {
    fontWeight: 950,
    fontSize: ".95rem",
    color: theme.palette.type === "dark" ? "#fff" : "#0F172A",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  opportunityMeta: {
    fontSize: ".74rem",
    color: "#64748B",
    marginTop: 3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  opportunityValue: {
    fontWeight: 950,
    fontSize: "1.2rem",
    color: "#0066FF",
    marginTop: theme.spacing(1.3)
  },
  progressTrack: {
    height: 8,
    background: "#E2E8F0",
    borderRadius: 999,
    overflow: "hidden",
    marginTop: theme.spacing(1.1)
  },
  progressFill: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg,#0066FF,#00A3FF)"
  },
  cardActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.3)
  },
  editBtn: {
    border: "1px solid rgba(0,102,255,0.24)",
    color: "#0066FF",
    borderRadius: 12,
    fontWeight: 900,
    textTransform: "none"
  },
  deleteBtn: {
    border: "1px solid rgba(239,68,68,0.24)",
    color: "#EF4444",
    borderRadius: 12,
    fontWeight: 900,
    textTransform: "none"
  },
  kanbanNavigator: {
    position: "sticky",
    top: 8,
    zIndex: 45,
    margin: theme.spacing(0.45, 1.5, 0.25),
    borderRadius: 999,
    background:
      theme.palette.type === "dark"
        ? "rgba(10,22,40,0.88)"
        : "rgba(255,255,255,0.92)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 4px 14px rgba(0,0,0,0.16)"
        : "0 4px 14px rgba(15,23,42,0.06)",
    overflow: "hidden"
  },

  kanbanNavigatorLabel: {
    padding: theme.spacing(0.9, 1.4, 0.45),
    fontSize: ".68rem",
    fontWeight: 900,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: ".08em"
  },

  kanbanNavigatorScroll: {
    overflowX: "auto",
    overflowY: "hidden",
    height: 14,
    padding: "0 6px",
    WebkitOverflowScrolling: "touch",
    overscrollBehaviorX: "contain",

    "&::-webkit-scrollbar": {
      height: 7
    },

    "&::-webkit-scrollbar-track": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(15,23,42,0.05)",
      borderRadius: 999
    },

    "&::-webkit-scrollbar-thumb": {
      background: "#0066FF",
      borderRadius: 999
    },

    "&::-webkit-scrollbar-thumb:hover": {
      background: "#0056D6"
    },

    scrollbarWidth: "thin",
    scrollbarColor: "#0066FF transparent"
  },

  kanbanNavigatorInner: {
    display: "flex",
    gap: theme.spacing(1.5),
    width: "max-content",
    minWidth: "100%"
  },

  kanbanNavigatorStage: {
    width: 320,
    minWidth: 320,
    maxWidth: 320,
    height: 34,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(0.7),
    padding: theme.spacing(0.35, 0.9),
    borderRadius: 10,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.035)"
        : "rgba(248,250,252,0.78)",
    border: `1px solid ${theme.palette.divider}`
  },

  kanbanNavigatorStageName: {
    fontSize: ".70rem",
    fontWeight: 900,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  kanbanNavigatorStageCount: {
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: ".62rem",
    fontWeight: 900,
    flexShrink: 0
  },

  kanbanBoard: {
    display: "flex",
    gap: theme.spacing(1.5),
    overflowX: "auto",
    overflowY: "hidden",
    padding: theme.spacing(1.5),
    paddingBottom: theme.spacing(2.5),
    scrollSnapType: "x proximity",
    WebkitOverflowScrolling: "touch",
    overscrollBehaviorX: "contain",
    scrollbarWidth: "none",

    "&::-webkit-scrollbar": {
      height: 0
    }
  },

  kanbanColumn: {
    width: 320,
    minWidth: 320,
    maxWidth: 320,
    borderRadius: 18,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.035)"
        : "rgba(248,250,252,.94)",
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.2),
    scrollSnapAlign: "start"
  },

  kanbanColumnHeader: {
    position: "sticky",
    top: 30,
    zIndex: 30,
    borderRadius: 14,
    padding: theme.spacing(1.2),
    marginBottom: theme.spacing(1.2),
    background:
      theme.palette.type === "dark"
        ? "rgba(15,23,42,0.96)"
        : "rgba(255,255,255,0.97)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 5px 15px rgba(0,0,0,0.18)"
        : "0 5px 15px rgba(15,23,42,0.06)"
  },

  kanbanColumnTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },

  kanbanColumnTitle: {
    fontWeight: 950,
    fontSize: ".86rem",
    color: theme.palette.text.primary
  },

  kanbanColumnCount: {
    minWidth: 26,
    height: 26,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: ".72rem"
  },

  kanbanColumnValue: {
    fontSize: ".72rem",
    color: theme.palette.text.secondary,
    marginTop: 5,
    fontWeight: 700
  },

  kanbanCards: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.1),
    minHeight: 120
  },

  kanbanEmpty: {
    borderRadius: 14,
    border: `1px dashed ${theme.palette.divider}`,
    padding: theme.spacing(3, 1.2),
    textAlign: "center",
    color: "#94A3B8",
    fontSize: ".75rem",
    fontWeight: 700
  },

  mobileScrollSpacer: {
    height: 80,
    flexShrink: 0
  }
}));

const statusConfig = {
  open: { label: "Em andamento", color: "#B8860B", bg: "rgba(255,200,87,0.12)", icon: <AccessTime style={{ fontSize: 14 }} /> },
  won: { label: "Ganho", color: "#00A86B", bg: "rgba(0,208,132,0.12)", icon: <CheckCircle style={{ fontSize: 14 }} /> },
  lost: { label: "Perdido", color: "#EF4444", bg: "rgba(255,77,77,0.12)", icon: <Cancel style={{ fontSize: 14 }} /> },
};

const Opportunities = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();

  const [opportunities, setOpportunities] = useState([]);
  const [stages, setStages] = useState([]);
  const [kanbanVisual, setKanbanVisual] = useState({
    title: "Pipeline",
    stageNames: {},
    canEdit: false
  });
  const [editingKanban, setEditingKanban] = useState(null);
  const [kanbanEditValue, setKanbanEditValue] = useState("");
  const [savingKanbanVisual, setSavingKanbanVisual] = useState(false);
  const [addingKanbanColumn, setAddingKanbanColumn] = useState(false);
  const [newKanbanColumnName, setNewKanbanColumnName] = useState("");
  const [mutatingKanbanColumn, setMutatingKanbanColumn] = useState(false);

  const kanbanBoardRef = useRef(null);
  const kanbanNavigatorRef = useRef(null);
  const kanbanSyncingRef = useRef(false);

  const syncKanbanScroll = (source, target) => {
    if (!source?.current || !target?.current) return;
    if (kanbanSyncingRef.current) return;

    kanbanSyncingRef.current = true;
    target.current.scrollLeft = source.current.scrollLeft;

    window.requestAnimationFrame(() => {
      kanbanSyncingRef.current = false;
    });
  };
  const [draggedOpportunityId, setDraggedOpportunityId] = useState(null);
  const [dragOverStageId, setDragOverStageId] = useState(null);
  const [movingOpportunity, setMovingOpportunity] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [period, setPeriod] = useState("month");
  const [dateFrom, setDateFrom] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [summary, setSummary] = useState({
    won: { count: 0, total: 0 },
    open: { count: 0, total: 0 },
    lost: { count: 0 },
    byUser: [],
    byDate: []
  });

  const contactIdFilter = new URLSearchParams(location.search).get("contactId");

  const formatCurrency = (val) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(val || 0));

  const fetchOpportunities = async () => {
    try {
      const pageSize = 50;
      let page = 1;
      let allOpportunities = [];
      let total = 0;

      do {
        const data = await getOpportunities({
          page,
          pageSize
        });

        const currentPage = data.opportunities || [];

        allOpportunities = [
          ...allOpportunities,
          ...currentPage
        ];

        total = Number(data.total || 0);

        if (
          currentPage.length === 0 ||
          allOpportunities.length >= total
        ) {
          break;
        }

        page += 1;
      } while (page <= 200);

      setOpportunities(allOpportunities);
    } catch (err) {
      console.error(
        "[Opportunities] erro ao carregar oportunidades:",
        err
      );

      toast.error("Erro ao carregar oportunidades");
    }
  };

  const fetchSummary = async () => {
    try {
      const data = await getOpportunitySummary(dateFrom, dateTo);
      setSummary(data || {});
    } catch (err) {
      console.log(err);
    }
  };

  const fetchStages = async () => {
    try {
      const data = await getOpportunityStages();
      setStages(data.stages || []);
    } catch (err) {
      toast.error("Erro ao carregar etapas do CRM");
    }
  };


  const fetchKanbanVisual = async () => {
    try {
      const data = await getKanbanVisualConfig();
      setKanbanVisual({
        title: data.title || "Pipeline",
        stageNames: data.stageNames || {},
        canEdit: Boolean(data.canEdit)
      });
    } catch (err) {
      toast.error("Erro ao carregar personalizacao do Kanban");
    }
  };

  const handleCreateKanbanColumn = async () => {
    if (!kanbanVisual.canEdit || mutatingKanbanColumn) return;

    const name = newKanbanColumnName.trim();

    if (!name || name.length > 70) {
      toast.error("Informe um nome valido para a coluna");
      return;
    }

    try {
      setMutatingKanbanColumn(true);
      await createKanbanStage({ name });
      await fetchStages();
      setNewKanbanColumnName("");
      setAddingKanbanColumn(false);
      toast.success("Coluna adicionada!");
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Erro ao adicionar coluna"
      );
    } finally {
      setMutatingKanbanColumn(false);
    }
  };

  const handleDeleteKanbanColumn = async (stage) => {
    if (
      !kanbanVisual.canEdit ||
      !stage.canDelete ||
      mutatingKanbanColumn
    ) return;

    if (!window.confirm(
      `Excluir a coluna "${kanbanVisual.stageNames?.[stage.name] || stage.name}"? Apenas colunas vazias podem ser excluidas.`
    )) return;

    try {
      setMutatingKanbanColumn(true);
      await deleteKanbanStage(stage.id);
      await fetchStages();
      toast.success("Coluna removida!");
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Erro ao excluir coluna"
      );
    } finally {
      setMutatingKanbanColumn(false);
    }
  };

  const cancelKanbanEdit = () => {
    setEditingKanban(null);
    setKanbanEditValue("");
  };

  const saveKanbanEdit = async (stageId = null) => {
    if (savingKanbanVisual || !kanbanVisual.canEdit) return;

    const name = kanbanEditValue.trim();
    const maxLength = stageId === null ? 80 : 70;

    if (!name || name.length > maxLength) {
      toast.error("Informe um nome valido");
      return;
    }

    try {
      setSavingKanbanVisual(true);

      const payload = stageId === null
        ? { title: name }
        : { stageId, label: name };

      const data = await saveKanbanVisualConfig(payload);

      setKanbanVisual({
        title: data.title || "Pipeline",
        stageNames: data.stageNames || {},
        canEdit: Boolean(data.canEdit)
      });

      cancelKanbanEdit();
      toast.success("Nome atualizado com sucesso!");
    } catch (err) {
      toast.error("Nao foi possivel salvar o nome");
    } finally {
      setSavingKanbanVisual(false);
    }
  };

  useEffect(() => {
    const oldBodyOverflowX = document.body.style.overflowX;
    const oldHtmlOverflowX = document.documentElement.style.overflowX;
    document.body.style.overflowX = "hidden";
    document.documentElement.style.overflowX = "hidden";

    return () => {
      document.body.style.overflowX = oldBodyOverflowX;
      document.documentElement.style.overflowX = oldHtmlOverflowX;
    };
  }, []);

  useEffect(() => {
    fetchOpportunities();
    fetchStages();
    fetchKanbanVisual();
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [dateFrom, dateTo]);

  const handlePeriod = (p) => {
    setPeriod(p);
    if (p === "week") {
      setDateFrom(moment().startOf("week").format("YYYY-MM-DD"));
      setDateTo(moment().endOf("week").format("YYYY-MM-DD"));
    }
    if (p === "month") {
      setDateFrom(moment().startOf("month").format("YYYY-MM-DD"));
      setDateTo(moment().endOf("month").format("YYYY-MM-DD"));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteOpportunity(id);
      toast.success("Removida!");
      setConfirmOpen(false);
      setToDelete(null);
      fetchOpportunities();
    } catch (err) {
      toast.error("Erro ao remover");
    }
  };

  const handleMoveOpportunity = async (
    opportunityId,
    targetStage
  ) => {
    if (!opportunityId || !targetStage || movingOpportunity) {
      return;
    }

    const currentOpportunity = opportunities.find(
      opportunity => Number(opportunity.id) === Number(opportunityId)
    );

    if (
      !currentOpportunity ||
      Number(currentOpportunity.stageId) === Number(targetStage.id)
    ) {
      setDraggedOpportunityId(null);
      setDragOverStageId(null);
      return;
    }

    setMovingOpportunity(true);

    const previousOpportunities = opportunities;

    setOpportunities(current =>
      current.map(opportunity =>
        Number(opportunity.id) === Number(opportunityId)
          ? {
              ...opportunity,
              stageId: targetStage.id,
              stage: targetStage,
              probability: targetStage.probability,
              status: targetStage.isWon
                ? "won"
                : targetStage.isLost
                ? "lost"
                : "open",
              lastStageChangeAt: new Date().toISOString()
            }
          : opportunity
      )
    );

    try {
      const updated = await updateOpportunity(
        opportunityId,
        {
          stageId: targetStage.id
        }
      );

      setOpportunities(current =>
        current.map(opportunity =>
          Number(opportunity.id) === Number(opportunityId)
            ? updated
            : opportunity
        )
      );

      toast.success(`Movido para ${targetStage.name}`);
      fetchSummary();
    } catch (error) {
      console.error(error);
      setOpportunities(previousOpportunities);
      toast.error("Não foi possível mover a oportunidade.");
    } finally {
      setMovingOpportunity(false);
      setDraggedOpportunityId(null);
      setDragOverStageId(null);
    }
  };

  const filteredOpportunities = useMemo(() => {
    return contactIdFilter
      ? opportunities.filter(o => String(o.contactId) === String(contactIdFilter) || String(o.contact?.id) === String(contactIdFilter))
      : opportunities;
  }, [opportunities, contactIdFilter]);

  const totalWon = filteredOpportunities.filter(o => o.status === "won").reduce((acc, o) => acc + Number(o.value || 0), 0);
  const totalOpen = filteredOpportunities.filter(o => o.status === "open").reduce((acc, o) => acc + Number(o.value || 0), 0);
  const totalLost = filteredOpportunities.filter(o => o.status === "lost").length;
  const totalCount = filteredOpportunities.length;
  const wonCount = filteredOpportunities.filter(o => o.status === "won").length;
  const conversion = totalCount > 0 ? Math.round((wonCount / totalCount) * 100) : 0;
  const sellersCount = summary.byUser?.length || 0;

  const opportunitiesByStage = useMemo(() => {
    const grouped = {};

    stages.forEach(stage => {
      grouped[String(stage.id)] = [];
    });

    filteredOpportunities.forEach(opportunity => {
      const stageKey = opportunity.stageId
        ? String(opportunity.stageId)
        : opportunity.stage?.id
        ? String(opportunity.stage.id)
        : null;

      if (stageKey && grouped[stageKey]) {
        grouped[stageKey].push(opportunity);
        return;
      }

      const firstStage = stages[0];

      if (firstStage) {
        grouped[String(firstStage.id)].push(opportunity);
      }
    });

    return grouped;
  }, [stages, filteredOpportunities]);

  return (
    <KoraxPage className={classes.pageRoot}>
      <ConfirmationModal
        title="Excluir oportunidade?"
        open={confirmOpen}
        onClose={() => { setConfirmOpen(false); setToDelete(null); }}
        onConfirm={() => handleDelete(toDelete)}
      >
        Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <OpportunityModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelected(null); }}
        opportunity={selected}
        onSaved={fetchOpportunities}
      />

      {contactIdFilter && (
        <div className={classes.filterNotice}>
          <Typography style={{ fontSize: ".82rem", fontWeight: 800, color: "#0066FF" }}>
            Mostrando oportunidades filtradas por contato
          </Typography>
          <Button size="small" onClick={() => history.push("/crm")}
            style={{ fontSize: ".75rem", fontWeight: 900, color: "#0066FF", textTransform: "none" }}>
            Limpar filtro
          </Button>
        </div>
      )}

      <KoraxPageHeader
        title="CRM"
        subtitle="Acompanhe seus contatos, oportunidades e resultados em um único lugar."
        actions={
          <KoraxButton
            variant="primary"
            startIcon={<Add />}
            onClick={() => {
              setSelected(null);
              setModalOpen(true);
            }}
          >
            Nova oportunidade
          </KoraxButton>
        }
      />

      <KoraxStatsGrid>
        <KoraxStatCard
          label="Vendas ganhas"
          value={formatCurrency(totalWon)}
          hint="Receita confirmada"
          badge={`${summary.won?.count || 0} negócios`}
          color="#16A34A"
          icon={<CheckCircle />}
          onClick={() => setActiveTab(0)}
        />

        <KoraxStatCard
          label="Pipeline aberto"
          value={formatCurrency(totalOpen)}
          hint="Valor em negociação"
          badge={`${summary.open?.count || 0} negócios`}
          color="#155EEF"
          icon={<AccessTime />}
          onClick={() => setActiveTab(0)}
        />

        <KoraxStatCard
          label="Oportunidades"
          value={totalCount}
          hint="Total registrado"
          badge={`${totalLost || 0} perdidas`}
          color="#D97706"
          icon={<TrendingUp />}
          onClick={() => setActiveTab(0)}
        />

        <KoraxStatCard
          label="Conversão"
          value={`${conversion}%`}
          hint="Desempenho comercial"
          badge="No período"
          color="#7C3AED"
          icon={<TrendingUp />}
        />
      </KoraxStatsGrid>

      <Paper className={classes.tabsShell} elevation={0}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          className={classes.tabsBar}
          TabIndicatorProps={{ className: classes.tabIndicator }}
        >
          <Tab label={kanbanVisual.title} className={classes.tab} />
          <Tab label="Oportunidades" className={classes.tab} />
          <Tab label="Desempenho" className={classes.tab} />
        </Tabs>

        {/* CRM OPPORTUNITIES LIST */}
        {activeTab === 1 && (
          <div className={classes.tableContent}>
            <div className={classes.tableActions}>
              <div>
                <Typography
                  style={{
                    fontWeight: 950,
                    fontSize: ".95rem"
                  }}
                >
                  Oportunidades
                </Typography>

                <Typography
                  style={{
                    fontSize: ".76rem",
                    color: "#64748B",
                    marginTop: 3
                  }}
                >
                  {filteredOpportunities.length} registro{filteredOpportunities.length !== 1 ? "s" : ""} no CRM
                </Typography>
              </div>

              <Button
                size="small"
                className={classes.addBtn}
                startIcon={<Add />}
                onClick={() => {
                  setSelected(null);
                  setModalOpen(true);
                }}
              >
                Nova oportunidade
              </Button>
            </div>

            {filteredOpportunities.length > 0 ? (
              <div className={classes.opportunitiesGrid}>
                {filteredOpportunities.map(opportunity => (
                  <OpportunityCard
                    key={opportunity.id}
                    opportunity={opportunity}
                    formatCurrency={formatCurrency}
                    onOpen={item => {
                      setSelected(item);
                      setModalOpen(true);
                    }}
                    onDelete={item => {
                      setToDelete(item.id);
                      setConfirmOpen(true);
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className={classes.emptyState}>
                Nenhuma oportunidade registrada ainda.
              </div>
            )}
          </div>
        )}

        {activeTab === 2 && (
          <div className={classes.dashboardContent}>
            <div className={classes.filterBar}>
              {[{ key: "week", label: "Esta semana" }, { key: "month", label: "Este mês" }, { key: "custom", label: "Personalizado" }].map(p => (
                <button
                  key={p.key}
                  className={`${classes.periodBtn} ${period === p.key ? classes.periodBtnActive : ""}`}
                  onClick={() => handlePeriod(p.key)}
                >
                  {p.label}
                </button>
              ))}

              {period === "custom" && (
                <>
                  <TextField label="De" type="date" size="small" variant="outlined" value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} className={classes.dateField} />
                  <TextField label="Até" type="date" size="small" variant="outlined" value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} className={classes.dateField} />
                  <Button size="small" variant="contained" onClick={fetchSummary}
                    style={{ borderRadius: 10, background: "#0066FF", color: "#fff", fontWeight: 900, textTransform: "none" }}>
                    Filtrar
                  </Button>
                </>
              )}
            </div>

            <div className={classes.kpiGrid}>
              <div className={classes.kpiCard}>
                <Avatar className={classes.kpiIcon} style={{ background: "rgba(0,208,132,0.12)", color: "#00A86B" }}><CheckCircle /></Avatar>
                <Typography className={classes.kpiNum} style={{ color: "#00A86B" }}>{formatCurrency(summary.won?.total)}</Typography>
                <Typography className={classes.kpiLabel}>Vendas ganhas</Typography>
                <Typography style={{ fontSize: ".72rem", fontWeight: 900, color: "#00A86B", marginTop: 4 }}>{summary.won?.count || 0} negócios</Typography>
              </div>

              <div className={classes.kpiCard}>
                <Avatar className={classes.kpiIcon} style={{ background: "rgba(0,102,255,0.1)", color: "#0066FF" }}><AccessTime /></Avatar>
                <Typography className={classes.kpiNum} style={{ color: "#0066FF" }}>{formatCurrency(summary.open?.total)}</Typography>
                <Typography className={classes.kpiLabel}>Em andamento</Typography>
                <Typography style={{ fontSize: ".72rem", fontWeight: 900, color: "#0066FF", marginTop: 4 }}>{summary.open?.count || 0} negócios</Typography>
              </div>

              <div className={classes.kpiCard}>
                <Avatar className={classes.kpiIcon} style={{ background: "rgba(255,77,77,0.1)", color: "#EF4444" }}><Cancel /></Avatar>
                <Typography className={classes.kpiNum} style={{ color: "#EF4444" }}>{summary.lost?.count || totalLost || 0}</Typography>
                <Typography className={classes.kpiLabel}>Perdidos</Typography>
              </div>

              <div className={classes.kpiCard}>
                <Avatar className={classes.kpiIcon} style={{ background: "rgba(245,158,11,0.12)", color: "#F59E0B" }}><TrendingUp /></Avatar>
                <Typography className={classes.kpiNum} style={{ color: "#F59E0B" }}>
                  {summary.won?.count + summary.open?.count + summary.lost?.count > 0
                    ? Math.round((summary.won?.count / (summary.won?.count + summary.open?.count + summary.lost?.count)) * 100)
                    : conversion}%
                </Typography>
                <Typography className={classes.kpiLabel}>Taxa de conversão</Typography>
              </div>
            </div>

            {(summary.byDate?.length > 0 || summary.byUser?.length > 0) ? (
              <div className={classes.chartsGrid}>
                <div className={classes.chartCard}>
                  <Typography className={classes.chartTitle}>📈 Receita no período</Typography>
                  {summary.byDate?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={summary.byDate}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => moment(d).format("DD/MM")} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                        <ReTooltip formatter={(v) => formatCurrency(v)} labelFormatter={(d) => moment(d).format("DD/MM/YYYY")} />
                        <Line type="monotone" dataKey="totalWon" name="Ganhos" stroke="#00A86B" strokeWidth={3} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className={classes.emptyState}>Sem receita no período.</div>
                  )}
                </div>

                <div className={classes.chartCard}>
                  <Typography className={classes.chartTitle}>👥 Ranking por vendedor</Typography>
                  {summary.byUser?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <ReBarChart data={summary.byUser} layout="vertical" margin={{ left: 8, right: 12 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                        <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                        <ReTooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="totalWon" name="Ganhos" fill="#00A86B" radius={[0,8,8,0]} />
                        <Bar dataKey="totalOpen" name="Em andamento" fill="#0066FF" radius={[0,8,8,0]} />
                      </ReBarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className={classes.emptyState}>Sem vendedores no período.</div>
                  )}
                </div>
              </div>
            ) : (
              <div className={classes.emptyState}>
                📊 Nenhum dado no período selecionado.
              </div>
            )}
          </div>
        )}

        {activeTab === 0 && (
          <div className={classes.tableContent}>
            <div className={classes.tableActions}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap"
              }}>
                <Typography style={{ fontWeight: 950, fontSize: ".95rem" }}>
                  {filteredOpportunities.length} oportunidade{filteredOpportunities.length !== 1 ? "s" : ""}
                </Typography>

              {editingKanban === "title" ? (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <TextField
                    autoFocus
                    size="small"
                    variant="outlined"
                    value={kanbanEditValue}
                    inputProps={{ maxLength: 80 }}
                    onChange={e => setKanbanEditValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        saveKanbanEdit();
                      }
                      if (e.key === "Escape") cancelKanbanEdit();
                    }}
                  />
                  <IconButton
                    size="small"
                    title="Salvar titulo"
                    disabled={savingKanbanVisual}
                    onClick={() => saveKanbanEdit()}
                  >
                    <CheckCircle fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Cancelar"
                    disabled={savingKanbanVisual}
                    onClick={cancelKanbanEdit}
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Typography style={{ fontWeight: 900, fontSize: ".9rem" }}>
                    {kanbanVisual.title}
                  </Typography>
                  {kanbanVisual.canEdit && (
                    <Tooltip title="Editar nome do funil">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingKanban("title");
                          setKanbanEditValue(kanbanVisual.title);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              )}

              </div>
              {kanbanVisual.canEdit && (
                addingKanbanColumn ? (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    flexWrap: "wrap"
                  }}>
                    <TextField
                      autoFocus
                      size="small"
                      variant="outlined"
                      placeholder="Nome da coluna"
                      value={newKanbanColumnName}
                      inputProps={{ maxLength: 70 }}
                      onChange={e => setNewKanbanColumnName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCreateKanbanColumn();
                        }
                        if (e.key === "Escape") {
                          setAddingKanbanColumn(false);
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      title="Salvar coluna"
                      disabled={mutatingKanbanColumn}
                      onClick={handleCreateKanbanColumn}
                    >
                      <CheckCircle fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Cancelar"
                      disabled={mutatingKanbanColumn}
                      onClick={() => {
                        setAddingKanbanColumn(false);
                        setNewKanbanColumnName("");
                      }}
                    >
                      <Cancel fontSize="small" />
                    </IconButton>
                  </div>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => setAddingKanbanColumn(true)}
                  >
                    Nova coluna
                  </Button>
                )
              )}
              <Button size="small" className={classes.addBtn} startIcon={<Add />} onClick={() => { setSelected(null); setModalOpen(true); }}>
                Nova oportunidade
              </Button>
            </div>

            <div className={classes.kanbanNavigator}>
              <div
                ref={kanbanNavigatorRef}
                className={classes.kanbanNavigatorScroll}
                onScroll={() =>
                  syncKanbanScroll(
                    kanbanNavigatorRef,
                    kanbanBoardRef
                  )
                }
              >
                <div
                  className={classes.kanbanNavigatorInner}
                  style={{
                    width:
                      stages.length > 0
                        ? `${stages.length * 320 + Math.max(0, stages.length - 1) * 12}px`
                        : "100%"
                  }}
                />
              </div>
            </div>

            <div
              ref={kanbanBoardRef}
              className={classes.kanbanBoard}
              onScroll={() =>
                syncKanbanScroll(
                  kanbanBoardRef,
                  kanbanNavigatorRef
                )
              }
            >
              {stages.map(stage => {
                const stageOpportunities =
                  opportunitiesByStage[String(stage.id)] || [];

                const stageValue = stageOpportunities.reduce(
                  (total, opportunity) =>
                    total + Number(opportunity.value || 0),
                  0
                );

                return (
                  <div
                    key={stage.id}
                    className={classes.kanbanColumn}
                    onDragOver={event => {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                      setDragOverStageId(stage.id);
                    }}
                    onDragLeave={event => {
                      if (!event.currentTarget.contains(event.relatedTarget)) {
                        setDragOverStageId(null);
                      }
                    }}
                    onDrop={event => {
                      event.preventDefault();

                      const opportunityId =
                        event.dataTransfer.getData("text/opportunity-id") ||
                        draggedOpportunityId;

                      handleMoveOpportunity(
                        Number(opportunityId),
                        stage
                      );
                    }}
                    style={{
                      borderTop: `4px solid ${stage.color || "#0066FF"}`,
                      background:
                        Number(dragOverStageId) === Number(stage.id)
                          ? `${stage.color || "#0066FF"}12`
                          : undefined,
                      boxShadow:
                        Number(dragOverStageId) === Number(stage.id)
                          ? `0 0 0 3px ${stage.color || "#0066FF"}22`
                          : undefined,
                      transition: "background .18s ease, box-shadow .18s ease"
                    }}
                  >
                    <div className={classes.kanbanColumnHeader}>
                      <div className={classes.kanbanColumnTitleRow}>

                        {editingKanban === `stage-${stage.id}` ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 3, minWidth: 0 }}>
                            <TextField
                              autoFocus
                              size="small"
                              variant="outlined"
                              value={kanbanEditValue}
                              inputProps={{ maxLength: 70 }}
                              style={{ width: 150 }}
                              onChange={e => setKanbanEditValue(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  saveKanbanEdit(stage.id);
                                }
                                if (e.key === "Escape") cancelKanbanEdit();
                              }}
                            />
                            <IconButton
                              size="small"
                              title="Salvar nome"
                              disabled={savingKanbanVisual}
                              onClick={() => saveKanbanEdit(stage.id)}
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Cancelar"
                              disabled={savingKanbanVisual}
                              onClick={cancelKanbanEdit}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </div>
                        ) : (
                          <>
                            <Typography className={classes.kanbanColumnTitle}>
                              {kanbanVisual.stageNames?.[stage.name] || stage.name}
                            </Typography>
                            {kanbanVisual.canEdit && (
                              <Tooltip title="Editar nome da coluna">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingKanban(`stage-${stage.id}`);
                                    setKanbanEditValue(
                                      kanbanVisual.stageNames?.[stage.name] || stage.name
                                    );
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}

                        {kanbanVisual.canEdit && stage.canDelete && (
                          <Tooltip title="Excluir coluna vazia">
                            <IconButton
                              size="small"
                              disabled={mutatingKanbanColumn}
                              onClick={() => handleDeleteKanbanColumn(stage)}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        <div
                          className={classes.kanbanColumnCount}
                          style={{
                            background: `${stage.color || "#0066FF"}18`,
                            color: stage.color || "#0066FF",
                            border: `1px solid ${stage.color || "#0066FF"}33`
                          }}
                        >
                          {stageOpportunities.length}
                        </div>
                      </div>

                      <Typography className={classes.kanbanColumnValue}>
                        {formatCurrency(stageValue)} em oportunidades
                      </Typography>
                    </div>

                    <div className={classes.kanbanCards}>
                      {stageOpportunities.map(opportunity => (
                        <div
                          key={opportunity.id}
                          draggable={!movingOpportunity}
                          onDragStart={event => {
                            setDraggedOpportunityId(opportunity.id);
                            event.dataTransfer.effectAllowed = "move";
                            event.dataTransfer.setData(
                              "text/opportunity-id",
                              String(opportunity.id)
                            );
                          }}
                          onDragEnd={() => {
                            setDraggedOpportunityId(null);
                            setDragOverStageId(null);
                          }}
                          style={{
                            opacity:
                              Number(draggedOpportunityId) === Number(opportunity.id)
                                ? 0.55
                                : 1,
                            cursor: movingOpportunity ? "wait" : "grab",
                            transition: "opacity .15s ease"
                          }}
                        >
                          <OpportunityCard
                            opportunity={opportunity}
                            formatCurrency={formatCurrency}
                          onOpen={item => {
                            setSelected(item);
                            setModalOpen(true);
                          }}
                            onDelete={item => {
                              setToDelete(item.id);
                              setConfirmOpen(true);
                            }}
                          />
                        </div>
                      ))}

                      {stageOpportunities.length === 0 && (
                        <div className={classes.kanbanEmpty}>
                          Nenhuma oportunidade nesta etapa.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {stages.length === 0 && (
                <div className={classes.emptyState}>
                  Nenhuma etapa configurada no CRM.
                </div>
              )}
            </div>
          </div>
        )}
      </Paper>

      <div className={classes.mobileScrollSpacer} />
    </KoraxPage>
  );
};

export default Opportunities;
